import { Result } from "../../api/types";
import { QTO_SOURCES } from "../../integrations/qto/registry";
import {
  buildLineItemsAddCsv,
  buildLineItemsUpdateCsv,
  type LineItemsCsvOptions,
} from "../../integrations/qto/reviewCsv";
import {
  inferWarnKind,
  parseOrderColumnReviewDataRow,
  type ReviewRow,
  type ReviewRowOnSheet,
} from "../../integrations/qto/reviewRow";
import { REVIEW_DATA_START_ROW, SHEET_NAME } from "./reviewSheetLayout";
import { appendQtoReviewLogAndDeleteRows, type AfterReviewSubmitWorkbookState } from "./reviewLog";
import { formatLineItemImportBatchTimestamp, importLineItemsCsv } from "../../integrations/qto/reviewClient";

async function readReviewSheetRows(options?: { visibleOnly?: boolean }): Promise<ReviewRowOnSheet[]> {
  const visibleOnly = options?.visibleOnly ?? false;
  const rows: ReviewRowOnSheet[] = [];

  await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItemOrNullObject(SHEET_NAME);
    await context.sync();
    if (sheet.isNullObject) return;

    const usedRange = sheet.getUsedRange();
    usedRange.load("rowCount");
    await context.sync();

    const startRow = REVIEW_DATA_START_ROW;
    if (usedRange.rowCount < startRow) return;

    const lastRow = usedRange.rowCount;

    const layoutHeaderRow = sheet.getRange("B4:N4");
    layoutHeaderRow.load("values");
    await context.sync();
    const layoutVals = layoutHeaderRow.values as string[][];
    const headerRow = layoutVals?.[0] ?? [];
    const orderAtC = String(headerRow[1] ?? "").trim() === "Order";
    const orderAtD = String(headerRow[2] ?? "").trim() === "Order";
    const linkAtC = String(headerRow[1] ?? "").trim() === "Link";
    const typeAtG = String(headerRow[5] ?? "").trim() === "Type";
    const typeAtH = String(headerRow[6] ?? "").trim() === "Type";
    const useOrderColumn = orderAtC || orderAtD;

    let sheetLayout: "v3" | "v3-spacer" | "v2" | "legacy";
    if (orderAtC && typeAtG) sheetLayout = "v3";
    else if (orderAtC && typeAtH) sheetLayout = "v3-spacer";
    else if (linkAtC) sheetLayout = "v2";
    else sheetLayout = "legacy";

    const dataLastCol = sheetLayout === "v3" || sheetLayout === "v2" ? "M" : sheetLayout === "v3-spacer" ? "N" : "J";
    const dataRange = sheet.getRange(
      useOrderColumn ? `B${startRow}:${dataLastCol}${lastRow}` : `B${startRow}:J${lastRow}`
    );
    dataRange.load("values");
    await context.sync();

    const values = dataRange.values as unknown[][];
    let hiddenFlags: boolean[] | null = null;
    if (visibleOnly) {
      const rowRanges: Excel.Range[] = [];
      for (let r = startRow; r <= lastRow; r++) {
        rowRanges.push(sheet.getRange(`${r}:${r}`));
      }
      rowRanges.forEach((rr) => rr.load("rowHidden"));
      await context.sync();
      hiddenFlags = rowRanges.map((rr) => rr.rowHidden === true);
    }

    for (let i = 0; i < values.length; i++) {
      if (visibleOnly && hiddenFlags![i]) continue;

      const row = values[i];
      const action = String(row[0] || "").trim();
      if (action !== "Add" && action !== "Update" && action !== "Ignore") continue;

      if (useOrderColumn) {
        const parsed = parseOrderColumnReviewDataRow(row, sheetLayout);
        const orderIdx = sheetLayout === "v3" || sheetLayout === "v3-spacer" ? 1 : 2;
        const takeoff0 = sheetLayout === "v3" || sheetLayout === "v3-spacer" ? 2 : 3;
        const linked0 = sheetLayout === "v3-spacer" ? 7 : 6;
        const lineItemName = String(row[linked0] || "");
        rows.push({
          action: action as ReviewRow["action"],
          link: parsed.link,
          warnKind: inferWarnKind(parsed.link, lineItemName),
          lineItemId: parsed.lineItemId,
          qtoId: parsed.qtoId,
          productId: parsed.productId,
          order: String(row[orderIdx] ?? "").trim(),
          takeoffName: String(row[takeoff0] || ""),
          takeoffQty: Number(row[takeoff0 + 1]) || 0,
          takeoffUom: String(row[takeoff0 + 2] || ""),
          lineItemName,
          lineItemQty: Number(row[linked0 + 1]) || 0,
          lineItemUom: String(row[linked0 + 2] || ""),
          excelRow: startRow + i,
        });
      } else {
        rows.push({
          action: action as ReviewRow["action"],
          link: "",
          lineItemId: "",
          qtoId: "",
          productId: "",
          order: "",
          takeoffName: String(row[2] || ""),
          takeoffQty: Number(row[3]) || 0,
          takeoffUom: String(row[4] || ""),
          lineItemName: String(row[5] || ""),
          lineItemQty: Number(row[6]) || 0,
          lineItemUom: String(row[7] || ""),
          excelRow: startRow + i,
        });
      }
    }
  });

  return rows;
}

/**
 * Read every row from the QTO Review sheet that has a valid Action value.
 * Returns an empty array when the sheet doesn't exist or has no data rows.
 */
export async function readReviewRows(options?: { visibleOnly?: boolean }): Promise<ReviewRow[]> {
  const withRow = await readReviewSheetRows(options);
  return withRow.map(({ excelRow: _r, ...rest }) => rest);
}

export async function exportReviewToEstimate(
  estimateId: string,
  options: LineItemsCsvOptions
): Promise<
  Result<{
    rowCount: number;
    /** Rows submitted as Add CSV (same as adds processed when success). */
    addRowCount: number;
    /** Rows submitted as Update CSV. */
    updateRowCount: number;
    reviewSheetEmpty?: boolean;
    canDeleteReviewSheet?: boolean;
  }>
> {
  const rowsOnSheet = await readReviewSheetRows({ visibleOnly: true });
  const actionable = rowsOnSheet.filter((r) => r.action === "Add" || r.action === "Update");

  if (actionable.length === 0) {
    return {
      success: false,
      error: new Error("No visible rows marked as Add or Update (hidden filter rows are not submitted)"),
    };
  }

  const sourceEntry = QTO_SOURCES[options.qtoSourceId];
  const batchTs = formatLineItemImportBatchTimestamp(new Date());
  // importBatchLabel, not label: these strings are already in customers' workbooks.
  const addImportBatch = options.importBatch ?? `${sourceEntry.importBatchLabel} Import - ${batchTs}`;
  const updateImportBatch = options.updateImportBatch ?? `${sourceEntry.importBatchLabel} Update - ${batchTs}`;
  const addFilename = options.addFilename ?? `${sourceEntry.csvMultipartBaseName}-add.csv`;
  const updateFilename = options.updateFilename ?? `${sourceEntry.csvMultipartBaseName}-update.csv`;

  const rowsForCsv = actionable.map(({ excelRow: _e, ...r }) => r);
  const adds = rowsForCsv.filter((r) => r.action === "Add");
  const updates = rowsForCsv.filter((r) => r.action === "Update");
  const updatesMissingLineItemId = updates.filter((r) => !r.lineItemId?.trim());
  if (updatesMissingLineItemId.length > 0) {
    return {
      success: false,
      error: new Error(
        "Every Update row must have a Line Item ID (hidden column). Fix or change those rows before submitting."
      ),
    };
  }

  if (adds.length > 0) {
    const addCsv = buildLineItemsAddCsv(rowsForCsv, addImportBatch);
    const addResult = await importLineItemsCsv(estimateId, addCsv, addFilename);
    if (!addResult.success) return addResult;
  }

  if (updates.length > 0) {
    const updateCsv = buildLineItemsUpdateCsv(rowsForCsv, updateImportBatch);
    const updateResult = await importLineItemsCsv(estimateId, updateCsv, updateFilename);
    if (!updateResult.success) {
      if (adds.length > 0) {
        return {
          success: false,
          error: new Error(
            `${updateResult.error?.message ?? "Update import failed"} New line items from this batch may already exist in ediphi; avoid submitting the same Add rows again.`
          ),
        };
      }
      return updateResult;
    }
  }

  let workbookAfter: AfterReviewSubmitWorkbookState | null = null;
  try {
    workbookAfter = await appendQtoReviewLogAndDeleteRows(actionable);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return {
      success: false,
      error: new Error(
        `Line items were imported, but updating the workbook failed (${msg}). Avoid submitting again unless you remove duplicate rows in ediphi.`
      ),
    };
  }

  const data: {
    rowCount: number;
    addRowCount: number;
    updateRowCount: number;
    reviewSheetEmpty?: boolean;
    canDeleteReviewSheet?: boolean;
  } = {
    rowCount: actionable.length,
    addRowCount: adds.length,
    updateRowCount: updates.length,
  };

  if (workbookAfter?.reviewSheetEmpty) {
    data.reviewSheetEmpty = true;
    data.canDeleteReviewSheet = workbookAfter.canDeleteReviewSheet;
  }

  return { success: true, data };
}
